import { BlockScrollStrategy, Overlay, OverlayConfig, OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType, PortalInjector, TemplatePortal } from '@angular/cdk/portal';
import { ComponentRef, Inject, Injectable, InjectionToken, Injector, Optional, SkipSelf, TemplateRef } from '@angular/core';
import { Keyboard } from '@core/models/common/keyboard';
import { Subject } from 'rxjs';
import { DialogRef } from './dialog-ref';
import { UIRouter } from '@uirouter/core';
import { DialogConfig } from './dialog-config';
import { DialogContainerComponent } from './dialog-container.component';
import { DialogSaveChangesComponent } from './save-changes/dialog-save-changes.component';
import { DialogConfirmComponent } from './confirm/dialog-confirm.component';
import { DialogDateComponent } from './date/dialog-date.component';
import { DialogLoaderComponent } from './loader/dialog-loader.component';
import { DialogAlertComponent } from './alert/dialog-alert.component';
import { DialogAttachmentComponent } from './attachment/dialog-attachment.component';
import { DialogPhoneVerificationComponent } from './phone-verification/dialog-phone-verification.component';

export const IHD_DIALOG_DATA = new InjectionToken<any>('OssDialogData');

/** Injection token that determines the scroll handling while the dialog is open. */
export const IHD_DIALOG_SCROLL_STRATEGY = new InjectionToken<
    () => ScrollStrategy
>('ihd-dialog-scroll-strategy');

/** @docs-private */
export function IHD_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY(
    overlay: Overlay
): () => BlockScrollStrategy {
    return () => overlay.scrollStrategies.block();
}

/** @docs-private */
export const IHD_DIALOG_SCROLL_STRATEGY_PROVIDER = {
    provide: IHD_DIALOG_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: IHD_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY,
};

@Injectable()
export class DialogService {
    private openDialogsAtThisLevel: DialogRef<any>[] = [];
    private afterAllClosedAtThisLevel = new Subject<void>();
    private afterOpenAtThisLevel = new Subject<DialogRef<any>>();
    private boundKeydown = this.handleKeydown.bind(this);

    /** Keeps track of the currently-open dialogs. */
    get openDialogs(): DialogRef<any>[] {
        return this.parentDialog
            ? this.parentDialog.openDialogs
            : this.openDialogsAtThisLevel;
    }

    /** Stream that emits when a dialog has been opened. */
    get afterOpen(): Subject<DialogRef<any>> {
        return this.parentDialog
            ? this.parentDialog.afterOpen
            : this.afterOpenAtThisLevel;
    }

    get afterAllClosed() {
        const parent = this.parentDialog;
        return parent ? parent.afterAllClosed : this.afterAllClosedAtThisLevel;
    }

    constructor(
        private overlay: Overlay,
        private injector: Injector,
        @Inject(IHD_DIALOG_SCROLL_STRATEGY) private _scrollStrategy,
        @Optional() private router: UIRouter,
        @Optional() @SkipSelf() private parentDialog: DialogService
    ) {
        // Close all of the dialogs when the user goes forwards/backwards in history or when the
        // location hash changes. Note that this usually doesn't include clicking on links (unless
        // the user is using the `HashLocationStrategy`).
        if (!parentDialog && this.router) {
            // _location.subscribe(() => this.closeAll());
        }
    }

    /**
     * Opens a dialog save changes confirmation.
     * @param config
     * @returns {DialogRef<DialogConfirmComponent>}
     */
    saveChanges(config: any): DialogRef<any> {
        return this.open(DialogSaveChangesComponent, config);
    }

    /**
     * Opens a dialog confirmation.
     * @param config
     * @returns {DialogRef<DialogConfirmComponent>}
     */
    confirm(config: any): DialogRef<any> {
        return this.open(DialogConfirmComponent, config);
    }

    /**
     * Opens a dialog date.
     * @param config
     * @returns {DialogRef<DialogDateComponent>}
     */
    setDate(config: any): DialogRef<any> {
        return this.open(DialogDateComponent, config);
    }

    /**
     * Opens a dialog loader.
     * @param config
     * @returns {DialogRef<DialogLoaderComponent>}
     */
    loader(config: any): DialogRef<any> {
        return this.open(DialogLoaderComponent, config);
    }

    /**
     * Opens a dialog alert.
     * @param config
     * @returns {DialogRef<DialogConfirmComponent>}
     */
    alert(config: any): DialogRef<any> {
        return this.open(DialogAlertComponent, config);
    }

    /**
     * Opens a dialog attachment.
     * @param config
     * @returns {DialogRef<DialogAttachmentComponent>}
     */
    openAttachment(config: any): DialogRef<any> {
        return this.open(DialogAttachmentComponent, config);
    }

    /**
     * Opens a modal dialog containing the given component.
     * @param componentOrTemplateRef Type of the component to load into the dialog,
     *     or a TemplateRef to instantiate as the dialog content.
     * @param config Extra configuration options.
     * @returns Reference to the newly-opened dialog.
     */
    open<T>(
        componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
        config?: DialogConfig | any
    ): DialogRef<T> {
        // const inProgressDialog = this.openDialogs.find(dialog => dialog._isAnimating());
        //
        // // If there's a dialog that is in the process of being opened, return it instead.
        // if (inProgressDialog) {
        //     return inProgressDialog;
        // }

        const dialogConfig = Object.assign(new DialogConfig(), config);

        // if (config.id && this.getDialogById(config.id)) {
        //     throw Error(`Dialog with id "${config.id}" exists already. The dialog id must be unique.`);
        // }

        const overlayRef = this.createOverlay(dialogConfig);
        const dialogContainer = this.attachDialogContainer(
            overlayRef,
            dialogConfig
        );
        const dialogRef = this.attachDialogContent(
            componentOrTemplateRef,
            dialogContainer,
            overlayRef,
            dialogConfig
        );

        // If this is the first dialog that we're opening, hide all the non-overlay content.
        // if (!this.openDialogs.length) {
        //     this._hideNonDialogContentFromAssistiveTechnology();
        // }

        if (!this.openDialogs.length) {
            document.addEventListener('keydown', this.boundKeydown);
        }

        this.openDialogs.push(dialogRef);
        dialogRef
            .afterClosed()
            .subscribe(() => this.removeOpenDialog(dialogRef));
        this.afterOpen.next(dialogRef);

        return dialogRef;
    }

    /**
     * Closes all of the currently-open dialogs.
     */
    closeAll(): void {
        let i = this.openDialogs.length;

        while (i--) {
            // The `_openDialogs` property isn't updated after close until the rxjs subscription
            // runs on the next microtask, in addition to modifying the array as we're going
            // through it. We loop through all of them and call close without assuming that
            // they'll be removed from the list instantaneously.
            this.openDialogs[i].close();
        }
    }

    /**
     * Finds an open dialog by its id.
     * @param id ID to use when looking up the dialog.
     */
    getDialogById(id: string): DialogRef<any> | undefined {
        return this.openDialogs.find((dialog) => dialog.id === id);
    }

    /**
     * Opens a dialog verification.
     * @param config
     * @returns {DialogRef<DialogVerificationComponent>}
     */
    verifyPhone(config: any): DialogRef<any> {
        return this.open(DialogPhoneVerificationComponent, config);
    }

    /**
     * Creates the overlay into which the dialog will be loaded.
     * @param config The dialog configuration.
     * @returns A promise resolving to the OverlayRef for the created overlay.
     */
    private createOverlay(config: DialogConfig): OverlayRef {
        let overlayState = this.getOverlayState(config);
        return this.overlay.create(overlayState);
    }

    /**
     * Creates an overlay state from a dialog config.
     * @param dialogConfig The dialog configuration.
     * @returns The overlay configuration.
     */
    private getOverlayState(dialogConfig: DialogConfig): OverlayConfig {
        let overlayState = new OverlayConfig();
        overlayState.panelClass = dialogConfig.panelClass;
        overlayState.hasBackdrop = dialogConfig.hasBackdrop;
        overlayState.scrollStrategy = this._scrollStrategy();
        overlayState.direction = dialogConfig.direction;
        if (dialogConfig.backdropClass) {
            overlayState.backdropClass = dialogConfig.backdropClass;
        }
        overlayState.positionStrategy = this.overlay.position().global();

        return overlayState;
    }

    /**
     * Attaches an DialogContainer to a dialog's already-created overlay.
     * @param overlay Reference to the dialog's underlying overlay.
     * @param config The dialog configuration.
     * @returns A promise resolving to a ComponentRef for the attached container.
     */
    private attachDialogContainer(
        overlay: OverlayRef,
        config: DialogConfig
    ): DialogContainerComponent {
        let containerPortal = new ComponentPortal(
            DialogContainerComponent,
            config.viewContainerRef
        );
        let containerRef: ComponentRef<DialogContainerComponent> = overlay.attach(
            containerPortal
        );
        containerRef.instance.config = config;
        return containerRef.instance;
    }

    /**
     * Attaches the user-provided component to the already-created DialogContainer.
     * @param componentOrTemplateRef The type of component being loaded into the dialog,
     *     or a TemplateRef to instantiate as the content.
     * @param dialogContainer Reference to the wrapping DialogContainer.
     * @param overlayRef Reference to the overlay in which the dialog resides.
     * @param config The dialog configuration.
     * @returns A promise resolving to the DialogRef that should be returned to the user.
     */
    private attachDialogContent<T>(
        componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
        dialogContainer: DialogContainerComponent,
        overlayRef: OverlayRef,
        config: DialogConfig
    ): DialogRef<T> {
        // Create a reference to the dialog we're creating in order to give the user a handle
        // to modify and close it.
        const dialogRef = new DialogRef<T>(
            overlayRef,
            dialogContainer,
            config.id
        );

        // When the dialog backdrop is clicked, we want to close it.
        if (config.hasBackdrop) {
            overlayRef.backdropClick().subscribe(() => {
                if (!dialogRef.disableClose) {
                    dialogRef.close();
                }
            });
        }

        if (componentOrTemplateRef instanceof TemplateRef) {
            dialogContainer.attachTemplatePortal(
                new TemplatePortal<T>(componentOrTemplateRef, null!, <any>{
                    $implicit: config.data,
                    dialogRef,
                })
            );
        } else {
            const injector = this.createInjector<T>(
                config,
                dialogRef,
                dialogContainer
            );
            const contentRef = dialogContainer.attachComponentPortal(
                new ComponentPortal(componentOrTemplateRef, undefined, injector)
            );
            dialogRef.componentInstance = contentRef.instance;
        }

        dialogRef
            .updateSize(config.width, config.height)
            .updatePosition(config.position);

        return dialogRef;
    }

    /**
     * Creates a custom injector to be used inside the dialog. This allows a component loaded inside
     * of a dialog to close itself and, optionally, to return a value.
     * @param config Config object that is used to construct the dialog.
     * @param dialogRef Reference to the dialog.
     * @param container Dialog container element that wraps all of the contents.
     * @returns The custom injector that can be used inside the dialog.
     */
    private createInjector<T>(
        config: DialogConfig,
        dialogRef: DialogRef<T>,
        dialogContainer: DialogContainerComponent
    ): PortalInjector {
        const userInjector =
            config &&
            config.viewContainerRef &&
            config.viewContainerRef.injector;
        const injectionTokens = new WeakMap();

        injectionTokens.set(DialogRef, dialogRef);
        injectionTokens.set(DialogContainerComponent, dialogContainer);
        injectionTokens.set(IHD_DIALOG_DATA, config.data || {});

        return new PortalInjector(
            userInjector || this.injector,
            injectionTokens
        );
    }

    /**
     * Removes a dialog from the array of open dialogs.
     * @param dialogRef Dialog to be removed.
     */
    private removeOpenDialog(dialogRef: DialogRef<any>) {
        const index = this.openDialogs.indexOf(dialogRef);

        if (index > -1) {
            this.openDialogs.splice(index, 1);

            // no open dialogs are left, call next on afterAllClosed Subject
            if (!this.openDialogs.length) {
                this.afterAllClosed.next();
                document.removeEventListener('keydown', this.boundKeydown);
            }
        }
    }

    /**
     * Handles global key presses while there are open dialogs. Closes the
     * top dialog when the user presses escape.
     */
    private handleKeydown(event: KeyboardEvent): void {
        const topDialog = this.openDialogs[this.openDialogs.length - 1];
        const canClose = topDialog ? !topDialog.disableClose : false;

        if (event.keyCode === Keyboard.ESC && canClose) {
            topDialog.close();
        }
    }
}
